
  import { defineComponent, PropType } from 'vue';
  import { mapGetters } from 'vuex';
  import { createColumnHelper } from '@tanstack/vue-table';

  import TableAggregated from '@/common/components/redesigned/Table/TableAggregated.vue';
  import { TFootRenderParams, TFootTableItem } from '@/common/components/redesigned/Table/types/tableTypes';
  import textMixin from '@/common/mixins/text';
  import { TStorePaths } from '@/common/types/storePaths';
  import { PDF_EXPORT_NO_WIDE_TABLE_SPLIT_ATTR } from '@/common/utils/pdfSplitWideTablesForClone';

  import { ANALYTICS_NOTES_MONTHLY_GETTERS_NAME } from '../../store/analyticsNotesMonthly/name';
  import { ANALYTICS_NOTES_YEARLY_GETTERS_NAME } from '../../store/analyticsNotesYearly/name';
  import { TAnalyticsNotesExportImportTableItem, TAnalyticsNotesFilter } from '../../types/analytics-notes';
  import { getCostTypeName } from '../../utils/options';

  export default defineComponent({
    name: 'AnalyticsNotesImportExportTable',
    components: {
      TableAggregated,
    },
    mixins: [textMixin],
    props: {
      filter: {
        type: Object as PropType<TAnalyticsNotesFilter>,
        required: true,
      },
      title: {
        type: String,
        required: true,
      },
      storePaths: {
        type: Object as PropType<TStorePaths>,
        required: true,
      },
      tableClass: {
        type: String,
        default: '',
      },
      loadStrategy: {
        type: String as PropType<'default' | 'infiniteScroll'>,
      },
      isMonthly: {
        type: Boolean,
        default: false,
      },
      isExport: {
        type: Boolean,
      },
    },
    computed: {
      ...mapGetters({
        getForeignTradeTemplateDataMonthly: ANALYTICS_NOTES_MONTHLY_GETTERS_NAME.getForeignTradeTemplateData,
        getForeignTradeTemplateDataYearly: ANALYTICS_NOTES_YEARLY_GETTERS_NAME.getForeignTradeTemplateData,
      }),
      pdfNoWideTableSplitAttrs(): Record<string, string> {
        return { [PDF_EXPORT_NO_WIDE_TABLE_SPLIT_ATTR]: '' };
      },
      getForeignTradeTemplateData() {
        return this.isMonthly ? this.getForeignTradeTemplateDataMonthly : this.getForeignTradeTemplateDataYearly;
      },
    },
    methods: {
      generateColumns() {
        const columnHelper = createColumnHelper<TAnalyticsNotesExportImportTableItem>();
        const costTypeName = getCostTypeName(this.filter.costType).toLowerCase();

        return [
          // Поставил игнорирования из-за того, что в браузере вылезает ошибка типа (после обновления ts должна уйти)
          // @ts-ignore
          columnHelper.accessor('code', {
            header: 'Код ТН ВЭД',
            size: 108,
          }),
          columnHelper.accessor('product', {
            header: 'Товар',
            size: 268,
            meta: {
              class: 'text-left analytics-notes-import-export-table__product-cell',
            },
          }),
          columnHelper.accessor(
            (row: TAnalyticsNotesExportImportTableItem) => this.formatNumber(row.price, 1, ' ', ','),
            {
              header: `Стоимость (${costTypeName})`,
              size: 220,
            },
          ),
          columnHelper.accessor(
            (row: TAnalyticsNotesExportImportTableItem) => this.formatNumber(row.share, 1, ' ', ','),
            {
              header: 'Доля, %',
              size: 108,
            },
          ),
        ];
      },

      footerRenderer({ meta }: TFootRenderParams<TAnalyticsNotesExportImportTableItem>): TFootTableItem[] {
        if (meta && meta.pageNumber !== meta.pagesTotal) {
          return [];
        }

        let totalCostKey = this.isExport ? 'exportValue' : 'importValue';

        // Для месячных данных данные за последний период берется из этого поля. Без постфикса "Cum" берется АППГ
        if (this.isMonthly) {
          totalCostKey = `${totalCostKey}Cum`;
        }

        const totalCost = this.getForeignTradeTemplateData[totalCostKey];

        return [
          { text: 'Итого:', colSpan: 2, class: 'text-left' },
          { text: this.formatNumber(totalCost, 1) },
          { text: this.formatNumber(100, 1) },
        ];
      },
    },
  });
